/**
 * LenderViewPreview — visual mockup of the proposed LENDER portfolio view.
 *
 * NOT a live page — a clickable static preview so the owner can approve the
 * layout before we build it for real (web + mobile, shared backend, demo).
 *
 * Route: /lender-view-preview  (no auth)
 *
 * The lender persona sees an overview of ALL their borrowers (loanees):
 *   - Portfolio KPIs: total committed, drawn, remaining to draw, loans expiring soon, compliance gaps
 *   - 10 clients, each with 1–12 projects (ongoing + completed)
 *   - Per client: loan totals, drawn %, remaining, next expiry, compliance status
 *   - Expand a client → per-project rows: status, loan, drawn %, remaining, draw
 *     history count, W-9/COI compliance, docs, loan expiry
 */
import React, { useMemo, useState } from 'react';
import {
  Landmark, Search, ChevronDown, ChevronRight, TrendingUp, Wallet, Clock,
  ShieldCheck, ShieldAlert, FileText, CheckCircle2, CircleDot, AlertTriangle,
  Building2, DollarSign,
} from 'lucide-react';

const NAVY = '#0b3b6f';
const GREEN = '#5c9e33';

// -------------------------------------------------------------------
// Brand mark — blue rounded-square "D" (matches app logo)
// -------------------------------------------------------------------
function BrandMark({ size = 34 }) {
  return (
    <div
      className="flex items-center justify-center font-extrabold text-white"
      style={{ width: size, height: size, background: '#2563EB', borderRadius: size * 0.26, fontSize: size * 0.52, lineHeight: 1 }}
    >
      D
    </div>
  );
}

// -------------------------------------------------------------------
// Mock data — 10 clients, each 1–12 projects
// -------------------------------------------------------------------
const CLIENT_NAMES = [
  'Morgan Builds LLC', 'Cedar Valley Developers', 'Riverstone Capital Homes', 'Oakwood Residential',
  'Summit Ridge Group', 'Hawkeye Construction Co.', 'Blue Line Properties', 'Prairie Gate Builders',
  'Ironwood Development', 'Northgate Homes',
];
const PROJ_TYPES = ['Single family', 'Duplex', '4-unit', 'Townhomes', 'Mixed-use', 'Commercial'];
const CITIES = ['Cedar Rapids, IA', 'Iowa City, IA', 'Coralville, IA', 'Marion, IA', 'North Liberty, IA'];

function seeded(i) {
  // deterministic pseudo-random from index
  const x = Math.sin(i * 99.7) * 10000;
  return x - Math.floor(x);
}

function buildClients() {
  return CLIENT_NAMES.map((name, ci) => {
    const projCount = 1 + Math.floor(seeded(ci + 1) * 12); // 1–12
    const projects = Array.from({ length: projCount }).map((_, pi) => {
      const loan = 150000 + Math.round(seeded(ci * 20 + pi + 3) * 850000);
      const completed = seeded(ci * 11 + pi + 9) > 0.72; // ~28% completed
      const drawnPct = completed ? 100 : Math.round(seeded(ci * 7 + pi + 1) * 96);
      const drawn = Math.round((loan * drawnPct) / 100);
      const remaining = loan - drawn;
      const draws = 1 + Math.floor(seeded(ci * 5 + pi) * 8);
      const w9 = seeded(ci + pi + 2) > 0.25;
      const coi = seeded(ci + pi + 5) > 0.35;
      const expiryDays = completed ? null : 20 + Math.floor(seeded(ci * 3 + pi) * 340);
      return {
        id: `${ci}-${pi}`,
        name: `${PROJ_TYPES[Math.floor(seeded(ci + pi) * PROJ_TYPES.length)]} · ${CITIES[Math.floor(seeded(ci * 2 + pi) * CITIES.length)]}`,
        loan, drawn, remaining, drawnPct, completed, draws, w9, coi, expiryDays,
      };
    });
    return { id: ci, name, projects };
  });
}

const money = (n) => `$${Math.round(n).toLocaleString()}`;
const moneyK = (n) => (n >= 1000000 ? `$${(n / 1000000).toFixed(1)}M` : `$${Math.round(n / 1000)}K`);

// -------------------------------------------------------------------
// Page
// -------------------------------------------------------------------
export default function LenderViewPreview() {
  const clients = useMemo(buildClients, []);
  const [open, setOpen] = useState({ 0: true });
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all'); // all | expiring | compliance

  const flat = useMemo(() => clients.flatMap((c) => c.projects.map((p) => ({ ...p, client: c.name }))), [clients]);
  const kpi = useMemo(() => {
    const committed = flat.reduce((s, p) => s + p.loan, 0);
    const drawn = flat.reduce((s, p) => s + p.drawn, 0);
    const remaining = flat.reduce((s, p) => s + p.remaining, 0);
    const ongoing = flat.filter((p) => !p.completed).length;
    const completed = flat.filter((p) => p.completed).length;
    const expiringSoon = flat.filter((p) => p.expiryDays != null && p.expiryDays <= 60).length;
    const complianceGaps = flat.filter((p) => !p.w9 || !p.coi).length;
    return { committed, drawn, remaining, ongoing, completed, expiringSoon, complianceGaps, projects: flat.length };
  }, [flat]);

  const visibleClients = useMemo(() => {
    const term = q.trim().toLowerCase();
    return clients
      .map((c) => {
        let projects = c.projects;
        if (filter === 'expiring') projects = projects.filter((p) => p.expiryDays != null && p.expiryDays <= 60);
        if (filter === 'compliance') projects = projects.filter((p) => !p.w9 || !p.coi);
        return { ...c, projects };
      })
      .filter((c) => c.projects.length > 0)
      .filter((c) => (term ? c.name.toLowerCase().includes(term) : true));
  }, [clients, q, filter]);

  return (
    <div className="min-h-screen" style={{ background: '#f1f5f9' }} data-testid="lender-view-preview">
      {/* Header */}
      <header className="text-white" style={{ background: NAVY }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-5 flex items-center gap-3">
          <BrandMark size={36} />
          <div className="flex-1">
            <p className="font-extrabold leading-none">DrawReady <span className="font-medium text-blue-200">· Lender</span></p>
            <p className="text-[11px] text-blue-200 mt-0.5">Portfolio overview</p>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-blue-200">Signed in as</p>
            <p className="text-sm font-semibold">First National Construction Lending</p>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 space-y-6">
        {/* KPI row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={Wallet} label="Total committed" value={moneyK(kpi.committed)} sub={`${kpi.projects} loans · ${clients.length} borrowers`} tone={NAVY} />
          <Kpi icon={DollarSign} label="Drawn to date" value={moneyK(kpi.drawn)} sub={`${Math.round((kpi.drawn / kpi.committed) * 100)}% of committed`} tone={GREEN} />
          <Kpi icon={TrendingUp} label="Available to draw" value={moneyK(kpi.remaining)} sub="Across all active loans" tone="#0ea5e9" />
          <Kpi icon={Clock} label="Expiring ≤ 60 days" value={String(kpi.expiringSoon)} sub={`${kpi.complianceGaps} compliance gaps`} tone="#f59e0b" alert />
        </div>

        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[220px] max-w-md relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search borrowers…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-200"
              data-testid="lender-search"
            />
          </div>
          {[['all', 'All'], ['expiring', 'Expiring soon'], ['compliance', 'Compliance gaps']].map(([k, l]) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`px-3.5 py-2 rounded-xl text-sm font-semibold border transition ${filter === k ? 'text-white' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              style={filter === k ? { background: NAVY, borderColor: NAVY } : {}}
              data-testid={`lender-filter-${k}`}
            >
              {l}
            </button>
          ))}
          <span className="ml-auto text-xs font-semibold text-slate-500">
            {kpi.ongoing} ongoing · {kpi.completed} completed
          </span>
        </div>

        {/* Clients */}
        <div className="space-y-3">
          {visibleClients.map((c) => (
            <ClientCard key={c.id} client={c} open={!!open[c.id]} onToggle={() => setOpen((o) => ({ ...o, [c.id]: !o[c.id] }))} />
          ))}
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------
function Kpi({ icon: Icon, label, value, sub, tone, alert }) {
  return (
    <div className={`rounded-2xl border bg-white p-4 ${alert ? 'border-amber-200' : 'border-slate-200'}`}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${tone}1a` }}>
          <Icon className="w-4 h-4" style={{ color: tone }} />
        </div>
        <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</p>
      </div>
      <p className="text-2xl font-extrabold text-slate-900 mt-2 tabular-nums">{value}</p>
      <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>
    </div>
  );
}

function ClientCard({ client, open, onToggle }) {
  const totals = useMemo(() => {
    const loan = client.projects.reduce((s, p) => s + p.loan, 0);
    const drawn = client.projects.reduce((s, p) => s + p.drawn, 0);
    const remaining = loan - drawn;
    const ongoing = client.projects.filter((p) => !p.completed).length;
    const completed = client.projects.filter((p) => p.completed).length;
    const gaps = client.projects.filter((p) => !p.w9 || !p.coi).length;
    const soon = client.projects.filter((p) => p.expiryDays != null && p.expiryDays <= 60).length;
    const nextExpiry = Math.min(...client.projects.filter((p) => p.expiryDays != null).map((p) => p.expiryDays));
    return { loan, drawn, remaining, ongoing, completed, gaps, soon, nextExpiry: isFinite(nextExpiry) ? nextExpiry : null };
  }, [client]);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`lender-client-${client.id}`}>
      {/* Client header */}
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-slate-50 text-left">
        {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0"><Building2 className="w-4 h-4" style={{ color: NAVY }} /></div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-slate-900 truncate">{client.name}</p>
          <p className="text-[11px] text-slate-400">{client.projects.length} projects · {totals.ongoing} ongoing · {totals.completed} completed</p>
        </div>
        {/* mini rollup */}
        <div className="hidden md:flex items-center gap-6 text-right">
          <MiniStat label="Committed" value={moneyK(totals.loan)} />
          <MiniStat label="Remaining" value={moneyK(totals.remaining)} tone={GREEN} />
          <div className="w-28">
            <div className="flex justify-between text-[10px] text-slate-400 mb-1"><span>Drawn</span><span>{Math.round((totals.drawn / totals.loan) * 100)}%</span></div>
            <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${(totals.drawn / totals.loan) * 100}%`, background: NAVY }} /></div>
          </div>
        </div>
        {/* badges */}
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {totals.gaps > 0
            ? <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200"><ShieldAlert className="w-3 h-3" /> {totals.gaps}</span>
            : <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><ShieldCheck className="w-3 h-3" /> OK</span>}
          {totals.soon > 0 && <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full bg-red-50 text-red-600 border border-red-200"><Clock className="w-3 h-3" /> {totals.soon}</span>}
        </div>
      </button>

      {/* Projects table */}
      {open && (
        <div className="border-t border-slate-100">
          <div className="hidden sm:grid grid-cols-[1.6fr_0.8fr_1.3fr_0.9fr_0.9fr_0.8fr] gap-2 px-4 py-2 bg-slate-50 text-[10px] font-bold uppercase tracking-wide text-slate-400">
            <span>Project</span><span>Status</span><span>Loan / drawn</span><span className="text-right">Remaining</span><span className="text-center">Compliance</span><span className="text-right">Expires</span>
          </div>
          {client.projects.map((p) => (
            <div key={p.id} className="grid grid-cols-2 sm:grid-cols-[1.6fr_0.8fr_1.3fr_0.9fr_0.9fr_0.8fr] gap-2 items-center px-4 py-3 border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
              <div className="min-w-0 col-span-2 sm:col-span-1">
                <p className="text-sm font-semibold text-slate-800 truncate">{p.name}</p>
                <p className="text-[11px] text-slate-400">{p.draws} draw{p.draws > 1 ? 's' : ''} · <FileText className="w-3 h-3 inline -mt-0.5" /> docs</p>
              </div>
              <div><StatusPill completed={p.completed} /></div>
              <div>
                <p className="text-sm font-semibold text-slate-800 tabular-nums">{money(p.loan)}</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full" style={{ width: `${p.drawnPct}%`, background: p.completed ? GREEN : NAVY }} /></div>
                  <span className="text-[10px] text-slate-400 w-8 text-right">{p.drawnPct}%</span>
                </div>
              </div>
              <p className="text-sm font-bold tabular-nums text-right" style={{ color: p.remaining > 0 ? GREEN : '#94a3b8' }}>{money(p.remaining)}</p>
              <div className="flex items-center justify-center gap-1">
                <DocDot ok={p.w9} label="W9" />
                <DocDot ok={p.coi} label="COI" />
              </div>
              <div className="text-right">
                {p.expiryDays == null
                  ? <span className="text-[11px] text-slate-400">—</span>
                  : <span className={`text-[11px] font-semibold ${p.expiryDays <= 60 ? 'text-red-600' : 'text-slate-500'}`}>{p.expiryDays}d</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, tone }) {
  return (
    <div>
      <p className="text-[10px] text-slate-400">{label}</p>
      <p className="text-sm font-bold tabular-nums" style={{ color: tone || '#0f172a' }}>{value}</p>
    </div>
  );
}

function StatusPill({ completed }) {
  return completed ? (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200"><CheckCircle2 className="w-3 h-3" /> Completed</span>
  ) : (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"><CircleDot className="w-3 h-3" /> Ongoing</span>
  );
}

function DocDot({ ok, label }) {
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded border ${ok ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-600 border-red-200'}`}
      title={ok ? `${label} on file` : `${label} missing`}
    >
      {ok ? <ShieldCheck className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />} {label}
    </span>
  );
}
